import React, { useCallback, useContext, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { useQuery } from "@tanstack/react-query";
import { isMobile } from "react-device-detect";
import { AuthContext } from "context";
import ProfileCompletionChecklist from "./ProfileCompletionChecklist";
import MyProfileForm from "./MyProfileForm";
import CompleteStripeAccountDialog from "./CompleteStripeAccountDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { useStripeAccount } from "hooks/stripe";
import { Role } from "types/user";
import { Loader } from "ui/Loader";
import { parseResponse } from "lib/fetch";
import { getDataleonSessionRequest } from "lib/api/profile";
import { isProviderBankDetailsComplete } from "../profileCompletion";

const MyProfil: React.FC = () => {
    const { account, updateAccount, currentRole } = useContext(AuthContext);
    const isProvider = currentRole === Role.PROVIDER;
    const isClient = currentRole === Role.CLIENT;
    const provider = account?.resources?.provider?.[0];
    const { data: stripeData, isLoading: stripeLoading } = useStripeAccount(
        isProvider ? provider?.stripe_id : undefined
    );
    const [hasDescription, setHasDescription] = useState(Boolean(provider?.description?.trim()));
    const [identityModalOpen, setIdentityModalOpen] = useState(false);
    const [bankModalOpen, setBankModalOpen] = useState(false);

    const {
        data: dataleonSession,
        isFetching: dataleonSessionLoading,
        isError: dataleonSessionError,
        refetch: refetchDataleonSession,
    } = useQuery({
        queryKey: ["dataleon", "session", account?.user.dataleon_task_id],
        queryFn: () => parseResponse(getDataleonSessionRequest()),
        enabled: false,
        staleTime: 0,
        gcTime: 0,
    });

    useEffect(() => {
        if (isProvider) {
            setHasDescription(Boolean(provider?.description?.trim()));
        }
    }, [isProvider, provider?.description]);

    useEffect(() => {
        if (identityModalOpen) {
            void refetchDataleonSession();
        }
    }, [identityModalOpen, refetchDataleonSession]);

    const handleOpenIdentityVerification = useCallback(async () => {
        if (isMobile) {
            const popup = window.open("about:blank", "_blank");
            const result = await refetchDataleonSession();
            const url = result.data?.url;

            if (url && popup) {
                popup.opener = null;
                popup.location.href = url;
                return;
            }

            popup?.close();
            return;
        }

        setIdentityModalOpen(true);
    }, [refetchDataleonSession]);

    if (!isProvider && !isClient) {
        return null;
    }

    if (isProvider && stripeLoading && !stripeData) {
        return <Loader />;
    }

    const stripeAccountId = provider?.stripe_id;
    const bankDetailsComplete = isProviderBankDetailsComplete(
        provider?.stripe_id,
        stripeData?.capabilities.transfers
    );
    const identityVerified = account?.user.dataleon_status === "approved";
    const hasPhone = Boolean(account?.user.phone?.trim());
    const hasPendingDataleonSession = Boolean(
        account?.user.dataleon_task_id && account?.user.dataleon_status !== "approved"
    );
    const needsStripeAccount = isProvider && !provider?.stripe_id;

    return (
        <div className="space-y-8">
            {isProvider && (
                <>
                    <ProfileCompletionChecklist
                        pictureId={account?.user.picture_id}
                        identityVerified={identityVerified}
                        bankDetailsComplete={Boolean(bankDetailsComplete)}
                        hasDescription={hasDescription}
                        hasPhone={hasPhone}
                        dataleonLink={account?.user.dataleon_link}
                        hasDataleonQrcode={hasPendingDataleonSession}
                        stripeAccountId={stripeAccountId}
                        onOpenIdentityModal={
                            hasPendingDataleonSession ? () => void handleOpenIdentityVerification() : undefined
                        }
                        onOpenBankModal={needsStripeAccount ? () => setBankModalOpen(true) : undefined}
                    />

                    {needsStripeAccount && (
                        <CompleteStripeAccountDialog
                            open={bankModalOpen}
                            onOpenChange={setBankModalOpen}
                        />
                    )}

                    {hasPendingDataleonSession && (
                        <Dialog open={identityModalOpen} onOpenChange={setIdentityModalOpen}>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Vérification de profil</DialogTitle>
                                </DialogHeader>
                                <p className="text-center text-sm text-muted-foreground">
                                    Veuillez scanner le QR code ci-dessous avec votre smartphone pour débuter la
                                    vérification&nbsp;:
                                </p>
                                <div className="mx-auto mt-4 flex min-h-[240px] max-w-[240px] items-center justify-center">
                                    {dataleonSessionLoading && <Loader />}
                                    {!dataleonSessionLoading && dataleonSessionError && (
                                        <p className="text-center text-sm text-destructive">
                                            Impossible de générer le QR code. Veuillez réessayer plus tard.
                                        </p>
                                    )}
                                    {!dataleonSessionLoading && dataleonSession?.url && (
                                        <QRCode value={dataleonSession.url} size={240} />
                                    )}
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </>
            )}

            <MyProfileForm onSaved={() => void updateAccount()} />
        </div>
    );
};

export default MyProfil;
